/**
 * Treatment Presented vs Accepted — V2 aggregation math.
 *
 * PURE FUNCTIONS ONLY. No Vue, no DOM, no moment, no lodash. Everything the V2
 * panel displays is derived here so it can be unit-tested in plain node
 * (cypress/node-tests/tx-presented-v2-math.test.js) instead of only in a browser.
 *
 * ── The contract that matters ────────────────────────────────────────────────
 * V2 must produce the SAME NUMBERS as V1 (AdvancedTxPlanned.vue) for the same
 * rows. V1's arithmetic, reproduced here exactly:
 *
 *   Tx Planned  = Σ NetFee over ALL rows            (not "rows without an appt")
 *   Scheduled   = Σ NetFee where AptStatus === 1
 *   Completed   = Σ NetFee where AptStatus === 2
 *   Acceptance  = (Scheduled + Completed) / Tx Planned × 100
 *
 * "Unscheduled" is new in V2 and is purely derived: Planned − Scheduled − Completed.
 * It is NOT a fourth query bucket.
 *
 * ── Two deliberate departures from V1 ───────────────────────────────────────
 * 1. Dates are parsed off the 'YYYY-MM-DD' string prefix rather than through
 *    moment(). The endpoint sets `dateStrings: true`, so DateTP always arrives as
 *    'YYYY-MM-DD'. moment() on that format yields local midnight, so .month() /
 *    .year() return the literal values — reading them off the string is identical
 *    and cannot drift with the browser timezone.
 * 2. NetFee is coerced with Number(). MySQL DECIMAL columns can arrive as strings
 *    depending on driver config, and V1's `_.sumBy` would silently concatenate
 *    them. Coercing a number is the identity operation, so parity holds either way.
 */

// Category colours for the three donuts. Index-stable: a category keeps the same
// colour across Tx Planned / Scheduled / Completed, which is the V1 bug this fixes
// (V1 assigns colours positionally per chart, so a category changes colour between
// the three pies).
const PALETTE = [
  '#4a48b8', '#5b5bd6', '#7676e0', '#9292ea',
  '#3b82f6', '#12805c', '#2fa07d', '#f59e0b',
  '#ec4899', '#8b5cf6', '#475569', '#94a3b8',
  '#0d9488', '#e5484d', '#c026d3', '#65a30d'
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SCHEDULED = 'Scheduled';
const COMPLETED = 'Completed';
const UNSCHEDULED = 'Unscheduled';

/**
 * OpenDental AptStatus → the three buckets V2 shows.
 *
 * 1 = Scheduled, 2 = Complete. Everything else — 0, null (no appointment at all,
 * via the LEFT JOIN), 3 UnschedList, 4 ASAP, 5 Broken, 6 Planned — is Unscheduled.
 *
 * Broken (5) landing in Unscheduled is INTENTIONAL: a broken appointment is not
 * accepted treatment, and V1 already renders it that way. Do not "fix" this.
 */
function classifyStatus(aptStatus) {
  if (aptStatus === 1) return SCHEDULED;
  if (aptStatus === 2) return COMPLETED;
  return UNSCHEDULED;
}

/** DECIMAL columns can arrive as strings; non-numeric garbage becomes 0, never NaN. */
function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Money rounded to cents, guarding against float noise like 4189.499999999999. */
function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Acceptance percentage. Returns 0 — never NaN, never Infinity — when nothing was
 * planned. A code filter can clip the denominator to zero while leaving scheduled
 * rows behind, so this guard is load-bearing, not defensive noise.
 */
function acceptancePct(accepted, planned) {
  if (!planned) return 0;
  return (accepted / planned) * 100;
}

/** Threshold colour used by every acceptance readout (ring, bar, heat cell). */
function accColor(pct) {
  if (pct >= 60) return '#12805c';
  if (pct >= 35) return '#a3620a';
  return '#e5484d';
}

/** 'Shabanaj, Stewart' → 'SS'. Single-token names fall back to their first two letters. */
function initials(name) {
  const parts = String(name || '').replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[1][0] + parts[0][0]).toUpperCase();
}

/**
 * Doctor / Hygienist from the provider result set. OpenDental convention:
 * IsSecondary 0 = provider (doctor), 1 = secondary provider (hygienist).
 * Anything else is unknown and grouped separately rather than guessed at.
 */
function roleOf(isSecondary) {
  if (isSecondary === 0) return 'Doctor';
  if (isSecondary === 1) return 'Hygienist';
  return 'Other';
}

/** 'YYYY-MM-DD…' → {year, monthIndex}. Returns null for anything unparseable. */
function parseYearMonth(dateStr) {
  const m = /^(\d{4})-(\d{2})/.exec(String(dateStr || ''));
  if (!m) return null;
  return { year: Number(m[1]), monthIndex: Number(m[2]) - 1 };
}

/**
 * The core roll-up. Every KPI tile, table total and comparison card runs through
 * this, so there is exactly one place the four stage numbers are computed.
 */
function aggregate(rows) {
  const t = {
    planned: 0, plannedC: 0,
    sched: 0, schedC: 0,
    comp: 0, compC: 0,
    unsched: 0, unschedC: 0,
    accepted: 0, acceptedC: 0,
    acc: 0
  };

  (rows || []).forEach((r) => {
    const fee = toNumber(r.NetFee);
    t.planned += fee;
    t.plannedC += 1;
    const status = classifyStatus(r.AptStatus);
    if (status === SCHEDULED) { t.sched += fee; t.schedC += 1; }
    else if (status === COMPLETED) { t.comp += fee; t.compC += 1; }
  });

  t.planned = round2(t.planned);
  t.sched = round2(t.sched);
  t.comp = round2(t.comp);
  t.accepted = round2(t.sched + t.comp);
  t.acceptedC = t.schedC + t.compC;
  t.unsched = round2(t.planned - t.accepted);
  t.unschedC = t.plannedC - t.acceptedC;
  t.acc = acceptancePct(t.accepted, t.planned);

  return t;
}

/**
 * One roll-up per provider, keyed on the display name (V1 groups on `Provider`
 * too, so the same name collisions — if any — behave identically).
 */
function byProvider(rows) {
  const groups = new Map();
  (rows || []).forEach((r) => {
    const name = r.Provider;
    if (!groups.has(name)) groups.set(name, { provider: name, provNum: r.ProvNum, rows: [] });
    groups.get(name).rows.push(r);
  });

  return [...groups.values()].map(g => Object.assign(
    { provider: g.provider, provNum: g.provNum },
    aggregate(g.rows)
  ));
}

/** Sorts a byProvider() result. 'planned' | 'acceptance' | 'name'. */
function sortProviders(list, key) {
  const out = (list || []).slice();
  if (key === 'name') return out.sort((a, b) => String(a.provider).localeCompare(String(b.provider)));
  if (key === 'acceptance') return out.sort((a, b) => b.acc - a.acc);
  return out.sort((a, b) => b.planned - a.planned);
}

// ============================================================================
// PATIENT-LEVEL ACCEPTANCE
//
// WHY THIS EXISTS — Paul Gilreath, 2026-08-24/25:
//
//   "Let's say you treatment plan 10 crowns. You schedule 2 of them. So now we
//    are at 20% acceptance when in fact the patient's plan is to do 2 every month
//    until finished… it would be nice to show the percentage of people who
//    scheduled at least 1 restorative appointment from their tx plan."
//
// `aggregate()` above is DOLLAR-weighted, so a practice presenting large phased
// plans scores worse than one presenting small ones even when every patient is
// actively moving forward. This counts PATIENTS instead: of the patients who had
// treatment presented, what share scheduled at least one real procedure.
//
// Both numbers are shown side by side on purpose — the gap between them IS the
// insight. Neither replaces the other.
// ============================================================================

/**
 * The floor for "this procedure has a fee".
 *
 * ⚠️ 0.01, NOT `!== 0`. OpenDental stores a denormalized zero as `1.11e-308`
 * (Batavia Family Dental has one in live data). It is truthy, it is `!== 0`, and
 * it survives every obvious guard — so a `!== 0` test here would quietly count
 * fee-less procedures on BOTH sides of this ratio.
 */
const FEE_EPSILON = 0.01;

function isFeeBearing(row) {
  return toNumber(row && row.NetFee) >= FEE_EPSILON;
}

/**
 * The office's own recall procedure codes, from OpenDental's `recalltype.Procedures`
 * — a comma-separated ProcCode list per recall type ('D0120,D1110', 'D4910', …).
 *
 * WHY THIS MATTERS: a patient shown a crown and a root canal who books nothing but
 * their next hygiene visit has accepted NO treatment. Counting that as a case start
 * is the same class of lie as the dollar metric Paul complained about, pointing the
 * other way.
 *
 * The country clause in huddle-service already strips `D0%`/`D1%` for USA-shaped
 * sites, so plain prophy (D1110/D1120), exams and x-rays never reach us. Two things
 * still get through, both confirmed against a live recalltype table:
 *   D1208  fluoride — explicitly allow-listed by that clause
 *   D4910  perio maintenance — `D4%`, so the clause passes it
 * And for CANADA / SOUTH_AFRICA the clause is a no-op (its `NOT LIKE a OR NOT LIKE b`
 * chain is always true), so those sites see every cleaning code there is.
 *
 * Read from the office's config rather than a hardcoded ADA list precisely because
 * of that last case — and because offices use custom codes.
 *
 * @param {Array} rows  result set 4: [{ Procedures: 'D0120,D1110' }, …]
 * @returns {Set} UPPERCASE ProcCodes
 */
function recallCodeSet(rows) {
  const out = new Set();
  (rows || []).forEach((r) => {
    const raw = r && (typeof r === 'string' ? r : r.Procedures);
    if (!raw) return;
    String(raw).split(',').forEach((code) => {
      const c = code.trim().toUpperCase();
      if (c) out.add(c);
    });
  });
  return out;
}

/**
 * Does this row represent treatment the patient had to say yes to?
 *
 * Fee-bearing AND not a recall procedure. Everything downstream — the buckets, the
 * denominator, the "X of Y procs" figure — is built on this one predicate, so the
 * card, the strip and the drawer cannot drift apart on what counts.
 *
 * An empty/absent recall set degrades to "fee-bearing only", which is the behaviour
 * before this existed. That matters during rollout: a frontend talking to a
 * huddle-service that predates result set 4 must still produce a sane number rather
 * than throwing.
 */
function isCountableTreatment(row, recallCodes) {
  if (!isFeeBearing(row)) return false;
  if (!recallCodes || !recallCodes.size) return true;
  return !recallCodes.has(String(row && row.ProcCode).trim().toUpperCase());
}

/** Patient buckets. `nofee` is excluded from the metric entirely — see byPatient(). */
const ALL_OF_IT = 'all';
const PARTIAL = 'partial';
const NOTHING = 'none';
// Named `nofee` for continuity, but it now means "nothing this patient was shown
// counts as treatment" — no fee, or recall-only, or both.
const NO_FEE = 'nofee';

/**
 * One roll-up per patient, keyed on PatNum.
 *
 * `planned` / `sched` / `comp` / `acc` mirror aggregate() over ALL of the
 * patient's rows, so the money on a patient row reconciles with the money in the
 * totals strip.
 *
 * The BUCKET, though, is decided over COUNTABLE procedures only — fee-bearing and
 * not recall (see isCountableTreatment). Two reasons, both the same mistake in
 * different directions:
 *   - a $0 exam left unscheduled must not demote a patient who booked every piece
 *     of real treatment they were shown;
 *   - booking nothing but the next cleaning must not read as accepting treatment.
 *
 * A patient with nothing countable at all gets `nofee`: they were never presented
 * treatment to accept, so leaving them in the denominator would invent a failure.
 * They are counted and reported separately, never silently folded in.
 *
 * @param {Array} rows
 * @param {Set} [recallCodes]  from recallCodeSet(); omit for fee-only behaviour
 */
function byPatient(rows, recallCodes) {
  const groups = new Map();

  (rows || []).forEach((r) => {
    const key = Number(r.PatNum);
    if (!groups.has(key)) {
      groups.set(key, { patNum: key, patient: r.Patient, provNums: new Set(), rows: [] });
    }
    const g = groups.get(key);
    g.rows.push(r);
    g.provNums.add(Number(r.ProvNum));
  });

  return [...groups.values()].map((g) => {
    const stats = aggregate(g.rows);

    const feeRows = g.rows.filter(r => isCountableTreatment(r, recallCodes));
    const feeAcceptedRows = feeRows.filter((r) => {
      const s = classifyStatus(r.AptStatus);
      return s === SCHEDULED || s === COMPLETED;
    });

    const feeProcs = feeRows.length;
    const feeAccepted = feeAcceptedRows.length;
    // How many of this patient's rows were set aside as recall. Surfaced so a
    // drawer can say "2 of 5 (1 recall visit not counted)" rather than looking
    // like it lost a row.
    const recallProcs = g.rows.filter(r => isFeeBearing(r) && !isCountableTreatment(r, recallCodes)).length;

    let bucket;
    if (!feeProcs) bucket = NO_FEE;
    else if (feeAccepted === feeProcs) bucket = ALL_OF_IT;
    else if (feeAccepted > 0) bucket = PARTIAL;
    else bucket = NOTHING;

    return Object.assign({
      patNum: g.patNum,
      patient: g.patient,
      provNums: [...g.provNums],
      feeProcs,
      feeAccepted,
      recallProcs,
      bucket
    }, stats);
  });
}

/**
 * Practice-level patient acceptance and the three buckets.
 *
 *   acc = (scheduled all of it + started, not finished) / (those two + nothing)
 *
 * `excluded` is the `nofee` count. It is deliberately OUTSIDE `patients`, so
 * `buckets.all + buckets.partial + buckets.none === patients` always holds and a
 * UI can state both numbers without them appearing not to add up.
 *
 * The money split exists because "started, not finished" is the case Paul is
 * arguing about: `partialAccepted of partialPlanned` is precisely the production
 * the dollar metric scores as a failure while the patient is mid-plan.
 */
function patientAcceptance(rows, recallCodes) {
  const list = byPatient(rows, recallCodes);

  const out = {
    patients: 0,
    started: 0,
    acc: 0,
    excluded: 0,
    // Of the excluded, how many were set aside because everything they were shown
    // was recall work. Reported separately from the plain fee-less case so the UI
    // can say WHY, which is the difference between a trustworthy number and a
    // number people argue with.
    excludedRecallOnly: 0,
    buckets: { all: 0, partial: 0, none: 0 },
    money: {
      allPlanned: 0,
      partialPlanned: 0,
      partialAccepted: 0,
      nonePlanned: 0
    }
  };

  list.forEach((p) => {
    if (p.bucket === NO_FEE) {
      out.excluded += 1;
      if (p.recallProcs) out.excludedRecallOnly += 1;
      return;
    }

    out.patients += 1;
    out.buckets[p.bucket] += 1;

    if (p.bucket === ALL_OF_IT) out.money.allPlanned += p.planned;
    else if (p.bucket === PARTIAL) {
      out.money.partialPlanned += p.planned;
      out.money.partialAccepted += p.accepted;
    } else out.money.nonePlanned += p.planned;
  });

  out.started = out.buckets.all + out.buckets.partial;
  // Reuses the shared guard, so an empty row set yields 0 rather than NaN.
  out.acc = acceptancePct(out.started, out.patients);

  out.money.allPlanned = round2(out.money.allPlanned);
  out.money.partialPlanned = round2(out.money.partialPlanned);
  out.money.partialAccepted = round2(out.money.partialAccepted);
  out.money.nonePlanned = round2(out.money.nonePlanned);

  return out;
}

/** Sorts a byPatient() result. 'planned' | 'acceptance' | 'name' — mirrors sortProviders. */
function sortPatients(list, key) {
  const out = (list || []).slice();
  if (key === 'name') return out.sort((a, b) => String(a.patient).localeCompare(String(b.patient)));
  if (key === 'acceptance') return out.sort((a, b) => b.acc - a.acc || b.planned - a.planned);
  return out.sort((a, b) => b.planned - a.planned);
}

/**
 * Category totals for the three donuts, plus the shared colour map.
 *
 * The map is built from the Tx Planned list (largest first), then any category
 * that only ever appears in Scheduled or Completed is appended. That ordering is
 * deterministic for a given row set, which is what keeps a category the same
 * colour in all three rings.
 */
function byCategory(rows) {
  const stage = (subset) => {
    const totals = new Map();
    subset.forEach((r) => {
      const name = r.ProcedureCategory || 'Uncategorized';
      const cur = totals.get(name) || { name, amount: 0, count: 0 };
      cur.amount += toNumber(r.NetFee);
      cur.count += 1;
      totals.set(name, cur);
    });
    return [...totals.values()]
      .map(c => ({ name: c.name, amount: round2(c.amount), count: c.count }))
      .sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));
  };

  const all = rows || [];
  const planned = stage(all);
  const sched = stage(all.filter(r => classifyStatus(r.AptStatus) === SCHEDULED));
  const comp = stage(all.filter(r => classifyStatus(r.AptStatus) === COMPLETED));

  const colorMap = {};
  let i = 0;
  [planned, sched, comp].forEach((list) => {
    list.forEach((c) => {
      if (colorMap[c.name] === undefined) {
        colorMap[c.name] = PALETTE[i % PALETTE.length];
        i += 1;
      }
    });
  });

  return { planned, sched, comp, colorMap };
}

/**
 * Per-procedure-code roll-up, used by the comparison cards' code table. Pass the
 * rows for one provider to get that provider's codes.
 */
function codeBreakdown(rows) {
  const groups = new Map();
  (rows || []).forEach((r) => {
    const code = r.ProcCode;
    if (!groups.has(code)) groups.set(code, { code, desc: r.Descript, rows: [] });
    groups.get(code).rows.push(r);
  });

  return [...groups.values()]
    .map(g => Object.assign({ code: g.code, desc: g.desc }, aggregate(g.rows)))
    .sort((a, b) => b.planned - a.planned || String(a.code).localeCompare(String(b.code)));
}

/**
 * Month-by-month presented vs accepted, grouped by calendar year, newest year first.
 *
 * Mirrors V1's getTxPlanTotalsAllYears: the year list runs from the range's END
 * year down to its START year, and every year carries all 12 months (months with
 * no activity render blank rather than being dropped) so the bar chart keeps a
 * stable 12-column axis.
 */
function monthlyByYear(rows, fromDate, toDate) {
  const from = parseYearMonth(fromDate);
  const to = parseYearMonth(toDate);
  if (!from || !to) return [];

  const years = [];
  for (let y = to.year; y >= from.year; y -= 1) years.push(y);

  // One pass over the rows instead of 12 × years passes (V1 re-filters the whole
  // set 24 times per year, which is the slowest thing in the panel on wide ranges).
  const buckets = new Map();
  (rows || []).forEach((r) => {
    const ym = parseYearMonth(r.DateTP);
    if (!ym) return;
    const key = ym.year + '-' + ym.monthIndex;
    const cur = buckets.get(key) || { planned: 0, accepted: 0 };
    const fee = toNumber(r.NetFee);
    cur.planned += fee;
    const status = classifyStatus(r.AptStatus);
    if (status === SCHEDULED || status === COMPLETED) cur.accepted += fee;
    buckets.set(key, cur);
  });

  return years.map((year) => {
    const months = MONTHS.map((label, monthIndex) => {
      const b = buckets.get(year + '-' + monthIndex) || { planned: 0, accepted: 0 };
      const planned = round2(b.planned);
      const accepted = round2(b.accepted);
      return {
        label,
        monthIndex,
        planned,
        accepted,
        acc: acceptancePct(accepted, planned),
        hasData: planned > 0 || accepted > 0
      };
    });

    const totPlanned = round2(months.reduce((a, m) => a + m.planned, 0));
    const totAccepted = round2(months.reduce((a, m) => a + m.accepted, 0));

    return {
      year,
      months,
      hasData: months.some(m => m.hasData),
      totals: {
        planned: totPlanned,
        accepted: totAccepted,
        acc: acceptancePct(totAccepted, totPlanned)
      }
    };
  });
}

/**
 * Client-side narrowing for the provider comparison picker.
 *
 * Providers are filtered here rather than server-side because the comparison view
 * switches between up to three of them and must not refetch. Codes and types are
 * accepted too so the UI can preview a selection, but the REAL code/type filter is
 * the SQL one — leaving those to the client would make wide date ranges run the
 * full query every time.
 *
 * An empty or missing list means "no filter on that field", never "match nothing".
 */
function filterRows(rows, options) {
  const o = options || {};
  // provNums is the RELIABLE provider key. The row's `Provider` is
  // concat(LName, ', ', FName) built in SQL, while the provider picker's options
  // carry FName/MI/LName/Suffix separately — reassembling the string to compare
  // would break on anyone with a middle initial or a suffix. Name matching stays
  // available for tests and for display-level grouping.
  const provNums = o.provNums && o.provNums.length
    ? new Set(o.provNums.map(Number))
    : null;
  const providers = o.providers && o.providers.length ? new Set(o.providers) : null;
  const codes = o.codes && o.codes.length ? new Set(o.codes) : null;
  const types = o.typeDefNums && o.typeDefNums.length
    ? new Set(o.typeDefNums.map(Number))
    : null;
  // Drives the "Who moved forward" bucket drill-through, which is a patient SET
  // rather than one provider. Follows the same empty-means-no-filter rule as the
  // others: an empty bucket must open an empty drawer, and it does — the caller
  // does not open the drawer at all for a bucket with no patients.
  const patNums = o.patNums && o.patNums.length
    ? new Set(o.patNums.map(Number))
    : null;

  if (!provNums && !providers && !codes && !types && !patNums) return (rows || []).slice();

  return (rows || []).filter((r) => {
    if (provNums && !provNums.has(Number(r.ProvNum))) return false;
    if (providers && !providers.has(r.Provider)) return false;
    if (codes && !codes.has(r.ProcCode)) return false;
    if (types && !types.has(Number(r.ProcedureCategoryDefNum))) return false;
    if (patNums && !patNums.has(Number(r.PatNum))) return false;
    return true;
  });
}

/**
 * The comparison picker holds at most `cap` providers. A pick beyond the cap
 * evicts the OLDEST selection rather than refusing the click — matching the
 * design, where a fourth pick swaps out the first.
 * Returns { selection, evicted } so the caller can explain what happened.
 */
function toggleCapped(current, value, cap) {
  const list = (current || []).slice();
  const i = list.indexOf(value);
  if (i >= 0) {
    list.splice(i, 1);
    return { selection: list, evicted: null };
  }
  if (cap && list.length >= cap) {
    const evicted = list.shift();
    list.push(value);
    return { selection: list, evicted };
  }
  list.push(value);
  return { selection: list, evicted: null };
}

/**
 * Y-axis for one year's bar chart: round the tallest bar UP to a clean number so
 * the four gridlines land on round values instead of on whatever the biggest
 * month happened to be.
 */
function chartAxis(months, compactFn) {
  const values = (months || []).map(m => Math.max(toNumber(m.planned), toNumber(m.accepted)));
  const max = Math.max(1, ...values);
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const fmt = compactFn || (n => String(Math.round(n)));
  return { top, axis: [1, 0.75, 0.5, 0.25, 0].map(f => (f === 0 ? '0' : fmt(top * f))) };
}

/** Sort comparator for the drill table. Default is most-recently-planned first. */
function sortDrillRows(rows, key) {
  return (rows || []).slice().sort((a, b) => {
    if (key === 'patient') return String(a.Patient || '').localeCompare(String(b.Patient || ''));
    if (key === 'fee') return toNumber(b.NetFee) - toNumber(a.NetFee);
    if (key === 'status') return String(a._status || '').localeCompare(String(b._status || ''));
    return String(b.DateTP || '').localeCompare(String(a.DateTP || ''));
  });
}

/** The windowed page numbers around the current page, clamped to what exists. */
function pageWindow(page, totalPages, span) {
  const width = span || 7;
  const from = Math.max(1, Math.min(page - Math.floor(width / 2), totalPages - (width - 1)));
  const out = [];
  for (let i = from; i < from + width && i <= totalPages; i += 1) out.push(i);
  return out;
}

/**
 * The drill drawer's whole pipeline in one pure pass: tag each row with its
 * bucket, count the buckets, filter by status and search, sort, then paginate.
 *
 * Lives here rather than as six computed properties on the component so the
 * behaviour that actually matters — the counts adding up, the search reaching
 * both patient and code, the page clamping when a filter shrinks the list — can
 * be tested in the gated node-test suite instead of only in a browser.
 */
function drillView(rows, options) {
  const o = options || {};
  const perPage = o.perPage || 10;

  const tagged = (rows || []).map((r, i) => Object.assign({}, r, {
    _status: classifyStatus(r.AptStatus),
    _key: `${r.ProcNum === undefined ? 'row' : r.ProcNum}-${i}`
  }));

  const counts = { all: tagged.length, Unscheduled: 0, Scheduled: 0, Completed: 0 };
  tagged.forEach((r) => { counts[r._status] += 1; });

  let filtered = tagged;
  if (o.status && o.status !== 'all') filtered = filtered.filter(r => r._status === o.status);

  const q = String(o.search || '').trim().toLowerCase();
  if (q) {
    filtered = filtered.filter(r =>
      `${r.Patient || ''} ${r.ProcCode || ''} ${r.Descript || ''}`.toLowerCase().indexOf(q) >= 0);
  }

  const sorted = sortDrillRows(filtered, o.sort);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  // Clamped, so filtering down from page 9 to a 2-page list can't strand the view
  // on an empty page.
  const page = Math.min(Math.max(1, o.page || 1), totalPages);
  const start = (page - 1) * perPage;

  return {
    counts,
    rows: sorted.slice(start, start + perPage),
    total,
    sum: round2(sorted.reduce((a, r) => a + toNumber(r.NetFee), 0)),
    page,
    totalPages,
    perPage,
    from: total ? start + 1 : 0,
    to: Math.min(start + perPage, total),
    pageWindow: pageWindow(page, totalPages, o.windowSize)
  };
}

/**
 * The filter validation ladder, lifted from V1's applyFilter() so the rules are
 * testable and identical.
 *
 * Returns `{ ok, message }` rather than firing a toast, so the same rules can be
 * asserted without a Vue instance. Every message here corresponds to a way the
 * query silently returns nothing, or takes long enough to hit the 30s Lambda
 * timeout.
 *
 * `ctx.now` is injectable so the "no future dates" and "within retention" rules
 * can be tested against a fixed clock.
 */
function validateFilters(filters, ctx) {
  const f = filters || {};
  const c = ctx || {};
  const maxYears = c.maxRangeYears === undefined ? 5 : c.maxRangeYears;
  const keepYears = c.yearsOfDataToKeep === undefined ? 4 : c.yearsOfDataToKeep;
  const fail = message => ({ ok: false, message });

  if ((f.procTypes || []).length && (f.procCodes || []).length) {
    return fail('You can not filter by Procedure Type AND Procedure Code. Please use only one of them.');
  }
  if (!f.from || !f.to) {
    return fail('Please select both start and end dates for the timeframe.');
  }

  const from = parseYearMonth(f.from);
  const to = parseYearMonth(f.to);
  if (!from || !to) return fail('Please select valid dates for the timeframe.');

  // Plain string comparison is safe and exact for zero-padded YYYY-MM-DD, and
  // avoids dragging a date library into a pure module.
  const start = String(f.from).slice(0, 10);
  const end = String(f.to).slice(0, 10);
  const now = String(c.now || '').slice(0, 10);

  if (start > end) {
    return fail('Start date cannot be after end date. Please select a valid date range.');
  }
  if (now && (start > now || end > now)) {
    return fail('Cannot select future dates. Please select dates up to today only.');
  }

  const spanYears = (Number(end.slice(0, 4)) - Number(start.slice(0, 4)))
    + (Number(end.slice(5, 7)) - Number(start.slice(5, 7))) / 12
    + (Number(end.slice(8, 10)) - Number(start.slice(8, 10))) / 365;
  if (spanYears > maxYears) {
    return fail(`Date range cannot exceed ${maxYears} years. Please select a smaller range.`);
  }

  if (now) {
    const floor = `${Number(now.slice(0, 4)) - keepYears}${now.slice(4, 10)}`;
    if (start < floor) {
      return fail(`Start date cannot be more than ${keepYears} years ago. Please select a more recent date range.`);
    }
  }

  return { ok: true, message: '' };
}

export {
  PALETTE, MONTHS, SCHEDULED, COMPLETED, UNSCHEDULED,
  FEE_EPSILON, ALL_OF_IT, PARTIAL, NOTHING, NO_FEE,
  classifyStatus, toNumber, round2, acceptancePct, accColor, initials, roleOf, parseYearMonth,
  aggregate, byProvider, sortProviders, byCategory, codeBreakdown, monthlyByYear,
  isFeeBearing, isCountableTreatment, recallCodeSet, byPatient, patientAcceptance, sortPatients,
  filterRows, toggleCapped, chartAxis, sortDrillRows, pageWindow, drillView, validateFilters
};
